import { createFeatureSelector, createSelector } from '@ngrx/store';
import { selectUserLanguage } from 'src/app/core/state/core.selectors';
import { SeraDropdownOption } from 'src/app/shared/models/SeraDropdownOption';
import { TranslationUtils } from 'src/app/shared/utils/translation-utils';
import { PsaAdmin } from '../models/PsaAdmin';
import { featureMasterDataKey, MasterDataState } from './master-data.state';
import { MM07PartType } from '../models/config-version/MM07PartType';
import { SelectListOption } from '@bmw-ds/components';

const selectMasterDataState = createFeatureSelector<MasterDataState>(featureMasterDataKey);

export const selectPlants = createSelector(selectMasterDataState, state => state?.plants ?? []);

export const selectPlantsDropdownOptions = createSelector(selectPlants, plants =>
    (plants ?? []).map(item => {
        return { id: item.plantCode, label: item.plantCode + ' ' + item.name } as SelectListOption;
    })
);

export const selectPlantsDropdownOptionsWithId = createSelector(selectPlants, plants =>
    (plants ?? []).map(item => {
        return { id: item.id.toString(), label: item.plantCode + ' ' + item.name } as SelectListOption;
    })
);

export const selectPlantsWithDisplayText = createSelector(selectPlants, plants => {
    return plants.map(item => ({ ...item, displayText: item.plantCode + ' ' + item.name }));
});

export const selectPlantsDropdownOptionsForEditUser = createSelector(selectPlantsWithDisplayText, plants =>
    plants.map(plant => {
        return { key: plant.id, name: plant.displayText } as SeraDropdownOption;
    })
);

export const selectPlantsSelectListOptionsForEditUser = createSelector(selectPlantsWithDisplayText, plants =>
    plants.map(plant => ({ id: String(plant.id), label: plant.displayText ?? '' } as SelectListOption))
);

export const selectTechnologies = createSelector(selectMasterDataState, state => state?.technologies ?? []);

export const selectTechnologiesDropdownOptions = (key: string) =>
    createSelector(selectTechnologies, technologies =>
        (technologies ?? []).map(technology => {
            return { id: technology[key], label: technology.technologyCode } as SelectListOption;
        })
    );

export const selectPsaAdmin = createSelector(selectMasterDataState, selectUserLanguage, (state, userLanguage) => {
    return (state?.psaAdmin ?? []).map((item: PsaAdmin) => {
        const psaAdmin: PsaAdmin = {
            ...item,
            displayText: TranslationUtils.translateTextValue(item.name, userLanguage ?? '')
        };
        return psaAdmin;
    });
});

export const selectPlantCCDCs = createSelector(selectMasterDataState, state => state?.plantsCCDC ?? []);

export const selectPlantCCDCsDropdownOptions = createSelector(selectPlantCCDCs, plantCCDCs => {
    return plantCCDCs?.map(plantCCDC => {
        return {
            id: plantCCDC.id.toString(),
            label: plantCCDC.displayName
        } as SelectListOption;
    });
});

export const selectPlantCCDCsDictionary = createSelector(selectPlantCCDCs, plantCCDCs => {
    return plantCCDCs?.reduce((acc, plantCCDC) => {
        acc[plantCCDC.id.toString()] = plantCCDC;
        return acc;
    }, {} as { [key: string]: (typeof plantCCDCs)[0] });
});

export const selectPlantCCDCDisplayTextById = (id: string) =>
    createSelector(selectPlantCCDCsDictionary, plantCCDCsDict => {
        const plantCCDC = plantCCDCsDict[id];
        return plantCCDC ? plantCCDC.displayName : null;
    });

export const selectPlantCCDCsDropdownOptionsForEditUser = createSelector(selectPlantCCDCs, plantCCDCs => {
    return plantCCDCs?.map(plantCCDC => {
        return {
            key: plantCCDC.id,
            name: plantCCDC.displayName
        } as SeraDropdownOption;
    });
});

export const selectPlantCCDCsSelectListOptionsForEditUser = createSelector(selectPlantCCDCs, plantCCDCs =>
    (plantCCDCs ?? []).map(plantCCDC => ({ id: String(plantCCDC.id), label: plantCCDC.displayName ?? '' } as SelectListOption))
);

export const selectTechnologiesDropDownOptionsCr11 = createSelector(selectTechnologies, selectUserLanguage, (technologies, userLanguage) => {
    return (technologies ?? []).map(technology => {
        return {
            id: technology.technologyCode,
            label: `${technology.technologyCode} ${technology.name?.['text' + userLanguage] ?? ''}`,
            disabled: !technology.active
        } as SelectListOption;
    });
});

export const selectGlobalDwsmConfig = createSelector(selectMasterDataState, state => {
    return state?.globalDwsmConfig;
});

export const selectActiveConfigVersion = createSelector(selectMasterDataState, state => state?.activeConfigVersion);

export const selectDefMM05 = createSelector(selectActiveConfigVersion, activeConfigVersion => activeConfigVersion?.defMM05);

export const selectDefMM05ForceCases = createSelector(selectDefMM05, defMM05 => defMM05?.forceCases);

export const selectDefMM05WristPostures = createSelector(selectDefMM05, defMM05 => defMM05?.wristPostures);

export const selectDefMM07 = createSelector(selectActiveConfigVersion, activeConfigVersion => activeConfigVersion?.defMM07);

export const selectDefMM07ExecutingConditions = createSelector(selectDefMM07, defMM07 => defMM07?.executionConditions);

export const selectDefMM07Parts = createSelector(selectDefMM07, defMM07 => defMM07?.parts);

export const selectDefMM07Postures = createSelector(selectDefMM07, defMM07 => defMM07?.postures);

export const selectPullAndPushExecutionConditionDropdownOptions = createSelector(selectDefMM07, selectUserLanguage, (state, userLanguage) => {
    return state?.executionConditions
        .filter(option => option.partType === MM07PartType.PULL_PUSH)
        .map(option => ({ label: TranslationUtils.translateTextValue(option.description, userLanguage), id: option.id.toString() } as SelectListOption));
});

export const selectTransferExecutionConditionDropdownOptions = createSelector(selectDefMM07, selectUserLanguage, (state, userLanguage) => {
    return state?.executionConditions
        .filter(option => option.partType === MM07PartType.TRANSFER)
        .map(option => ({ label: TranslationUtils.translateTextValue(option.description, userLanguage), id: option.id.toString() } as SelectListOption));
});

export const selectDefMM04 = createSelector(selectActiveConfigVersion, activeConfigVersion => activeConfigVersion?.defMM04);

export const selectDefMM04Postures = createSelector(selectDefMM04, defMM04 => defMM04?.postures);

export const selectLoadingPlantCCDC = createSelector(selectMasterDataState, state => state?.loadingPlantCCDC ?? false);

export const selectBuildings = createSelector(selectMasterDataState, state => state?.buildings ?? []);

export const selectBuildingsDropdownOptions = createSelector(selectBuildings, buildings => {
    return buildings?.map(building => {
        return {
            id: building.buildingCode,
            label: building.buildingCode
        } as SelectListOption;
    });
});

export const selectProductLineCodes = createSelector(selectMasterDataState, state => state?.productLines);

export const selectProductLineCodesDropdownOptions = createSelector(selectProductLineCodes, productLines => {
    return productLines?.reduce((acc, item) => {
        if (!acc.some(opt => opt.id === item.productLineCode)) {
            acc.push({ id: item.productLineCode, label: item.productLineCode } as SelectListOption);
        }
        return acc;
    }, [] as SelectListOption[]);
});

export const selectDerivativeCodes = createSelector(selectMasterDataState, state => state?.derivatives);

export const selectDerivativeCodesDropdownOptions = createSelector(selectDerivativeCodes, derivativeCodes => {
    return derivativeCodes?.reduce((acc, item) => {
        if (!acc.some(opt => opt.id === item.derivativeCode)) {
            acc.push({ id: item.derivativeCode, label: item.derivativeCode } as SelectListOption);
        }
        return acc;
    }, [] as SelectListOption[]);
});
